import React, {useEffect, useReducer, useRef, useState, useCallback} from "react";
import {Button} from 'react-bootstrap';
import InputField from "../../InputField";
import ModalWidget from "../ModalWidget";
import {ReactComponent as GradingSystemSvg} from "../../../assets/svg/pen-border.svg";
import {ReactComponent as PlusSvg} from "../../../assets/svg/plus.svg";
import {ReactComponent as SaveSvg} from "../../../assets/svg/save.svg";
import {ReactComponent as TrashSvg} from "../../../assets/svg/trash.svg";
import NumberBoxInput from "../../NumberBoxInput";
import GradeScoreInput from "../../grading/GradeScoreInput";
import {GradingSystem} from "../../../models/GradingSystem";
import FieldItem from "../../FieldItem";
import DropdownField from "../../DropdownField";
import {createGradeScheme, updateGradeScheme} from "../../../services/GradeSchemeService";
import {createRubric, updateRubric} from "../../../services/RubricService";
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent} from '@dnd-kit/core';
import {SortableContext, useSortable, verticalListSortingStrategy, arrayMove} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {useUser} from "../../../contexts/UserContext";
import FormInputError from "../../FormInputError";

interface GradingSystemGrade {
    name: string;
    score: {
        min_score: any;
        max_score: any;
    };
    comment?: string;
}

interface GradingSystemAssessment {
    name: string;
    allocation: {
        allocation_percent: number;
    };
}

interface GradingSystemBehaviour {
    name: string;
}

interface GradingSystemData {
    name: string;
    grades: GradingSystemGrade[];
    assessments: GradingSystemAssessment[];
    behaviours: GradingSystemBehaviour[];
    uuid?: string;
    is_not_school_owned?: boolean;
    [key: string]: any;
}

export type GradingItemMode = 'grading_scheme' | 'rubric';

interface AddGradingSystemProps {
    gradingSystem?: GradingSystemData;
    show?: boolean;
    onHide?: () => void;
    onAdded?: (data: any) => void;
    actionTitle?: string;
    mode?: GradingItemMode;
    existingNames?: string[];
    templateNames?: string[];
}

const modeLabels: Record<GradingItemMode, {
    title: string;
    nameLabel: string;
    namePlaceholder: string;
    defaultAction: string;
    ownedDesc: string;
    defaultDesc: string;
    scoreLabel: string;
}> = {
    grading_scheme: {
        title: 'Grading Scheme',
        nameLabel: 'Grading Scheme Name',
        namePlaceholder: 'Enter Grading Scheme Name',
        defaultAction: 'Save Grading Scheme',
        ownedDesc: 'This is a customized grading scheme owned by the school. Any changes made will reflect in all classes it belongs to.',
        defaultDesc: 'This is one of the default grading schemes by Gradde. It can also be customized to fit your school\u2019s system.',
        scoreLabel: 'Score Allocation',
    },
    rubric: {
        title: 'Rubric',
        nameLabel: 'Rubric Name',
        namePlaceholder: 'Enter Rubric Name',
        defaultAction: 'Save Rubric',
        ownedDesc: 'This rubric defines how scores are weighted and calculated for classrooms it belongs to.',
        defaultDesc: 'This is one of the default rubrics by Gradde. It can also be customized to fit your school\u2019s system.',
        scoreLabel: 'Category Weights',
    },
};

const gradingTypeOptions = [
    { value: 'category_weighted', label: 'Category Weighted' },
    { value: 'assessment_weighted', label: 'Assessment Weighted' },
    { value: 'points_based', label: 'Points Based' },
];

const gradingTypeDescriptions: Record<string, string> = {
    category_weighted: 'Assessments are grouped into categories (e.g. CA, Exam). Each category\'s average is weighted by the percentages you set below.',
    assessment_weighted: 'Each assessment carries its own weight. No categories needed — useful when assessments have varying importance.',
    points_based: 'Total points earned ÷ total points possible. No categories or weights — all marks count equally.',
};

let draftIdCounter = 0;
function nextDraftId() { return `draft-${++draftIdCounter}`; }

function SortableFieldItem({ id, children }: { id: string; children: (sortableProps: { listeners: any; attributes: any; setNodeRef: any; style: any }) => React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return <>{children({ listeners, attributes, setNodeRef, style })}</>;
}

interface ShowSettings {
    gradeScore: boolean;
    scoreAllocation: boolean;
    extras: boolean;
}

interface SettingsAction {
    type: string;
    data: boolean;
}

const defaultGradingSystem: GradingSystemData = {
    "name": "",
    "grades": [
        { "name": "A", "score": { "min_score": 0, "max_score": 0 } },
        { "name": "B", "score": { "min_score": 0, "max_score": 0 } },
        { "name": "C", "score": { "min_score": 0, "max_score": 0 } },
        { "name": "D", "score": { "min_score": 0, "max_score": 0 } },
        { "name": "F", "score": { "min_score": 0, "max_score": 0 } }
    ],
    "assessments": [
        { "name": "Assessment 1", "allocation": { "allocation_percent": 20 } },
        { "name": "Assessment 2", "allocation": { "allocation_percent": 20 } },
        { "name": "Exam", "allocation": { "allocation_percent": 60 } },
    ],
    "behaviours": []
}

const settingsReducer = (showSettings: ShowSettings, action: SettingsAction): ShowSettings => {
    switch (action.type) {
        case GradingSystem.GRADE_SCORE:
            return { ...showSettings, gradeScore: action.data }
        case GradingSystem.SCORE_ALLOCATION:
            return { ...showSettings, scoreAllocation: action.data }
        case GradingSystem.EXTRAS:
            return { ...showSettings, extras: action.data }
        default:
            return showSettings
    }
}

function AddGradingSystem({
    gradingSystem = defaultGradingSystem,
    show = false,
    onHide = ()=>{},
    onAdded = ()=>{},
    actionTitle,
    mode = 'grading_scheme',
    existingNames = [],
    templateNames = []
}: AddGradingSystemProps) {
    const labels = modeLabels[mode];
    const resolvedActionTitle = actionTitle ?? labels.defaultAction;
    const { user } = useUser();
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'completed'>('idle');
    const completedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [showSettings, setShowSettings] = useReducer(settingsReducer, {
        gradeScore: false,
        scoreAllocation: false,
        extras: false
    })
    const [gradeSystemName, setGradeSystemName] = useState<string>(gradingSystem.name)

    // For grading_scheme: initialise from grades or from scheme (template format)
    const initialGrades: GradingSystemGrade[] =
        gradingSystem.grades ??
        ((gradingSystem as any).scheme ?? []).map((b: any) => ({
            name: b.grade,
            score: { min_score: b.min, max_score: b.max },
            comment: b.comment || undefined,
        }));

    // For rubric: initialise from assessments or from category_weights (template format)
    const categoryWeightsEntries = Object.entries((gradingSystem as any).category_weights ?? {});
    const initialAssessments: GradingSystemAssessment[] =
        (gradingSystem.assessments && gradingSystem.assessments.length > 0)
            ? gradingSystem.assessments
            : categoryWeightsEntries.length > 0
                ? categoryWeightsEntries.map(([name, pct]) => ({
                    name,
                    allocation: { allocation_percent: Number(pct) },
                  }))
                : [];

    const [gradeSystemGrades, setGradeSystemGrades] = useState<GradingSystemGrade[]>(initialGrades)
    const [gradeSystemAssessments, setGradeSystemAssessments] = useState<GradingSystemAssessment[]>(initialAssessments)
    const [minPassing, setMinPassing] = useState<number | null>(
        (gradingSystem as any).min_passing ?? null
    )
    const [enforceMinPassing, setEnforceMinPassing] = useState<boolean>(
        ((gradingSystem as any).min_passing ?? null) !== null
    )
    const [gradingType, setGradingType] = useState<string>((gradingSystem as any).grading_type || 'points_based')
    const [error, setError] = useState<Record<string, string> | string | null>(null);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
        };
    }, []);

    // Reset saveState when modal is re-opened
    useEffect(() => {
        if (show) setSaveState('idle');
    }, [show]);

    const handleGradeChange = (gradeIndex: number, updatedScores: any) => {
        setGradeSystemGrades((prevGrades) =>
            prevGrades.map((grade, index) =>
                index === gradeIndex ? { ...grade, score: updatedScores } : grade
            )
        );
    };

    const handleGradeCommentChange = (gradeIndex: number, comment: string) => {
        setGradeSystemGrades((prevGrades) =>
            prevGrades.map((grade, index) =>
                index === gradeIndex ? { ...grade, comment } : grade
            )
        );
    };

    function setShowSettingsType(type: string, value: boolean){
        setShowSettings({ type: type, data: value })
    }

    const onAddGradingSystem = async ()=>
    {
        try {
            const schoolId = user?.school?.id ?? user?.school?.uuid;
            if (!schoolId && !(gradingSystem.uuid || gradingSystem.id)) {
                setError('School information is missing. Please refresh and try again.');
                return;
            }

            // ── Frontend name validation ──
            const trimmedName = gradeSystemName.trim().toLowerCase();
            if (!trimmedName) {
                setError({ name: 'Name is required' });
                return;
            }
            const isEditing = !!(gradingSystem.uuid ?? gradingSystem.id) && !gradingSystem.is_not_school_owned;
            const originalName = gradingSystem.name?.trim().toLowerCase();

            // Check against Gradde template names
            if (templateNames.some(n => n.trim().toLowerCase() === trimmedName)) {
                setError({ name: 'This name is reserved by a Gradde template' });
                return;
            }
            // Check against existing school-owned items (skip own name when editing)
            if (existingNames.some(n => {
                const normalized = n.trim().toLowerCase();
                if (isEditing && normalized === originalName) return false;
                return normalized === trimmedName;
            })) {
                const itemLabel = mode === 'grading_scheme' ? 'grading scheme' : 'rubric';
                setError({ name: `A ${itemLabel} with this name already exists` });
                return;
            }

            setSaveState('saving');
            setError(null);

            if (mode === 'grading_scheme') {
                // Validate grade entries
                const gradeErrors: Record<string, string> = {};
                const seenGrades: Record<string, number> = {};
                gradeSystemGrades.forEach((grade, i) => {
                    const nameLower = grade.name.trim().toLowerCase();
                    if (!nameLower) {
                        gradeErrors[`grades.${i}.name`] = 'Grade letter is required';
                    } else if (nameLower in seenGrades) {
                        gradeErrors[`grades.${i}.name`] = `Duplicate grade letter '${grade.name}'`;
                    } else {
                        seenGrades[nameLower] = i;
                    }
                    const minVal = Number(grade.score.min_score);
                    const maxVal = Number(grade.score.max_score);
                    if (isNaN(minVal) || isNaN(maxVal)) {
                        gradeErrors[`grades.${i}.name`] = gradeErrors[`grades.${i}.name`] || 'Min and max scores must be numbers';
                    } else if (minVal < 0 || maxVal < 0) {
                        gradeErrors[`grades.${i}.name`] = gradeErrors[`grades.${i}.name`] || 'Scores cannot be negative';
                    } else if (maxVal > 100) {
                        gradeErrors[`grades.${i}.name`] = gradeErrors[`grades.${i}.name`] || 'Max score cannot exceed 100';
                    } else if (minVal > maxVal) {
                        gradeErrors[`grades.${i}.name`] = gradeErrors[`grades.${i}.name`] || 'Min score cannot be greater than max score';
                    }
                });
                if (gradeSystemGrades.length === 0) {
                    gradeErrors['grades'] = 'At least one grade is required';
                }
                if (Object.keys(gradeErrors).length > 0) {
                    setSaveState('idle');
                    setError(gradeErrors);
                    return;
                }

                const scheme = gradeSystemGrades.map((grade) => ({
                    grade: grade.name,
                    min: Number(grade.score.min_score),
                    max: Number(grade.score.max_score),
                    ...(grade.comment ? { comment: grade.comment } : {}),
                }));
                let res: any;
                const existingId = gradingSystem.uuid ?? gradingSystem.id;
                if (existingId && !gradingSystem.is_not_school_owned) {
                    res = await updateGradeScheme(existingId, { name: gradeSystemName, scheme });
                } else {
                    res = await createGradeScheme(schoolId!, gradeSystemName, scheme);
                }
                setSaveState('completed');
                completedTimerRef.current = setTimeout(() => {
                    onHide();
                    onAdded(res.data);
                }, 1500);
            } else {
                // Validate rubric categories
                if (gradingType === 'category_weighted') {
                    const catErrors: Record<string, string> = {};
                    const seenCats: Record<string, number> = {};
                    let totalWeight = 0;
                    gradeSystemAssessments.forEach((a, i) => {
                        const nameLower = a.name.trim().toLowerCase();
                        if (!nameLower) {
                            catErrors[`assessments.${i}.name`] = 'Category name is required';
                        } else if (nameLower in seenCats) {
                            catErrors[`assessments.${i}.name`] = `Duplicate category '${a.name}'`;
                        } else {
                            seenCats[nameLower] = i;
                        }
                        const pct = Number(a.allocation.allocation_percent);
                        if (isNaN(pct) || pct < 0) {
                            catErrors[`assessments.${i}.allocation_percent`] = 'Weight must be a non-negative number';
                        }
                        totalWeight += isNaN(pct) ? 0 : pct;
                    });
                    if (gradeSystemAssessments.length === 0) {
                        catErrors['assessments'] = 'At least one category is required';
                    } else if (Math.abs(totalWeight - 100) > 0.01) {
                        catErrors['assessments'] = `Category weights must sum to 100% (currently ${Math.round(totalWeight)}%)`;
                    }
                    if (Object.keys(catErrors).length > 0) {
                        setSaveState('idle');
                        setError(catErrors);
                        return;
                    }
                }

                const categoryWeights = gradingType === 'category_weighted'
                    ? Object.fromEntries(
                        gradeSystemAssessments.map((a) => [a.name, Number(a.allocation.allocation_percent)])
                      )
                    : {};
                let res: any;
                const effectiveMinPassing = enforceMinPassing ? (minPassing ?? 50) : null;
                if (gradingSystem.id && !gradingSystem.is_not_school_owned) {
                    res = await updateRubric(gradingSystem.id, {
                        name: gradeSystemName,
                        category_weights: categoryWeights,
                        min_passing: effectiveMinPassing,
                        grading_type: gradingType,
                    });
                } else {
                    res = await createRubric(schoolId!, gradeSystemName, categoryWeights, effectiveMinPassing, gradingType);
                }
                setSaveState('completed');
                completedTimerRef.current = setTimeout(() => {
                    onHide();
                    onAdded(res.data);
                }, 1500);
            }
        }
        catch(err: any)
        {
            setSaveState('idle');
            setError(err.response?
                err.response.data.errors: 'Oops... an error occurred, try again later')
        }
    }

    const [editingAll, setEditingAll] = useState<{ gradeScore: boolean; scoreAllocation: boolean }>({
        gradeScore: false,
        scoreAllocation: false,
    });

    // ── Draft state for settings modals ──
    const [draftGrades, setDraftGrades] = useState<(GradingSystemGrade & { _id: string })[]>([]);
    const [draftAssessments, setDraftAssessments] = useState<(GradingSystemAssessment & { _id: string })[]>([]);

    // Initialise drafts when settings modals open
    useEffect(() => {
        if (showSettings.gradeScore) {
            setDraftGrades(gradeSystemGrades.map(g => ({ ...g, _id: nextDraftId() })));
            setEditingAll(prev => ({ ...prev, gradeScore: false }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSettings.gradeScore]);

    useEffect(() => {
        if (showSettings.scoreAllocation) {
            setDraftAssessments(gradeSystemAssessments.map(a => ({ ...a, _id: nextDraftId() })));
            setEditingAll(prev => ({ ...prev, scoreAllocation: false }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSettings.scoreAllocation]);

    // Commit drafts on save
    const handleSaveGradeSettings = useCallback(() => {
        setGradeSystemGrades(draftGrades.map(({ _id, ...rest }) => rest));
        setEditingAll(prev => ({ ...prev, gradeScore: false }));
        setShowSettingsType(GradingSystem.GRADE_SCORE, false);
    }, [draftGrades]);

    const handleSaveAssessmentSettings = useCallback(() => {
        setGradeSystemAssessments(draftAssessments.map(({ _id, ...rest }) => rest));
        setEditingAll(prev => ({ ...prev, scoreAllocation: false }));
        setShowSettingsType(GradingSystem.SCORE_ALLOCATION, false);
    }, [draftAssessments]);

    // Draft handlers
    const handleDraftFieldChange = (type: string, index: number, newValue: string) => {
        if (type === GradingSystem.GRADE_SCORE) {
            setDraftGrades(prev => prev.map((g, i) => i === index ? { ...g, name: newValue } : g));
        } else if (type === GradingSystem.SCORE_ALLOCATION) {
            setDraftAssessments(prev => prev.map((a, i) => i === index ? { ...a, name: newValue } : a));
        }
    };

    const handleDraftDelete = (type: string, index: number) => {
        if (type === GradingSystem.GRADE_SCORE) {
            setDraftGrades(prev => prev.filter((_, i) => i !== index));
        } else if (type === GradingSystem.SCORE_ALLOCATION) {
            setDraftAssessments(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleDraftDeleteAll = (type: string) => {
        if (type === GradingSystem.GRADE_SCORE) {
            setDraftGrades([]);
        } else if (type === GradingSystem.SCORE_ALLOCATION) {
            setDraftAssessments([]);
        }
    };

    const handleDraftAdd = (type: string, newFieldData?: any) => {
        const newField = { ...(newFieldData || {}), _id: nextDraftId() };
        if (type === GradingSystem.GRADE_SCORE) {
            setDraftGrades(prev => [...prev, newField]);
        } else if (type === GradingSystem.SCORE_ALLOCATION) {
            setDraftAssessments(prev => [...prev, newField]);
        }
    };

    // DnD
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEndGrades = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setDraftGrades(prev => {
                const oldIndex = prev.findIndex(g => g._id === active.id);
                const newIndex = prev.findIndex(g => g._id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const handleDragEndAssessments = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setDraftAssessments(prev => {
                const oldIndex = prev.findIndex(a => a._id === active.id);
                const newIndex = prev.findIndex(a => a._id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    return (
        <>
            <ModalWidget
                show={show}
                onHide={onHide}
                onBack={onHide}
                title={labels.title}
                description= {
                    gradingSystem.is_not_school_owned?
                        labels.defaultDesc :
                        labels.ownedDesc
                }
                actionTitle={resolvedActionTitle}
                actionIcon={SaveSvg}
                headerIcon={GradingSystemSvg}
                isScrollable={true}
                navigateBack={false}
                onAction={onAddGradingSystem}
                actionLoading={saveState === 'saving'}
                actionCompleted={saveState === 'completed'}
            >
                <InputField label={labels.nameLabel}
                            initialValue={gradingSystem.name}
                            onValueChange={(value: string) => {
                                setGradeSystemName(value)
                            }}
                            placeholder={labels.namePlaceholder}
                            errorMessage={(error as any)?.name}/>

                {/*Add grade score – only for grading scheme*/}
                {mode === 'grading_scheme' && (
                <>
                    <div className='mt-4 d-flex justify-content-between align-items-center'>
                        <div className="font-xs font-bold">Grade - Score</div>

                        <Button variant="outline-dark"
                                onClick={()=>setShowSettingsType(GradingSystem.GRADE_SCORE, true)}
                                className='font-bold'
                                size="sm">
                            Settings
                        </Button>
                    </div>

                    {(error as any)?.grades &&
                        (Array.isArray((error as any).grades)
                            ? (error as any).grades.map((gradeError: string, i: number) => <FormInputError key={i} className="mb-1">{gradeError}</FormInputError>)
                            : <FormInputError className="mb-1">{(error as any).grades}</FormInputError>
                        )
                    }

                    {
                        gradeSystemGrades.map((grade, index)=>(
                            <GradeScoreInput key={index} className='mt-3' grade={grade.name}
                                minScore={grade.score.min_score} maxScore={grade.score.max_score}
                                comment={grade.comment}
                                 errorMessage={error? (error as any)[`grades.${index}.name`]: null}
                                 onMinScoreChange={(value: any) =>
                                     handleGradeChange(index, {
                                         min_score: value,
                                         max_score: grade.score.max_score,
                                     })
                                 }
                                 onMaxScoreChange={(value: any) =>
                                     handleGradeChange(index, {
                                         min_score: grade.score.min_score,
                                         max_score: value,
                                     })
                                 }
                                 onCommentChange={(value: string) =>
                                     handleGradeCommentChange(index, value)
                                 }

                            />
                        ))
                    }
                </>
                )}

                {/*Grading type and min passing – only for rubric*/}
                {mode === 'rubric' && (
                <>
                    <DropdownField
                        className='mt-4'
                        label='Grading Type'
                        options={gradingTypeOptions}
                        value={gradingType}
                        onChange={(option: any) => setGradingType(option.value)}
                    />
                    {gradingType && gradingTypeDescriptions[gradingType] && (
                        <div className='mt-2 font-xs' style={{ color: '#6c757d' }}>
                            {gradingTypeDescriptions[gradingType]}
                        </div>
                    )}
                </>
                )}

                {/*Category weights – only for category_weighted rubrics*/}
                {mode === 'rubric' && gradingType === 'category_weighted' && (
                <>
                    <div className='mt-4 d-flex justify-content-between align-items-center'>
                        <div className="font-xs font-bold">
                            {labels.scoreLabel}
                        </div>

                        <Button variant="outline-dark"
                                onClick={() => setShowSettingsType(GradingSystem.SCORE_ALLOCATION, true)}
                                className='font-bold'
                                size="sm">
                            Settings
                        </Button>
                    </div>

                    {(error as any)?.assessments &&
                        (Array.isArray((error as any).assessments)
                            ? (error as any).assessments.map((assessmentError: string, i: number) => <FormInputError key={i} className="mb-1">{assessmentError}</FormInputError>)
                            : <FormInputError className="mb-1">{(error as any).assessments}</FormInputError>
                        )
                    }

                    {
                        gradeSystemAssessments.map((assessment, assessmentIndex)=>(
                            <div className='mt-3 d-flex align-items-center'>
                                <div className='font-bold'>
                                    {assessment.name}
                                </div>

                                <NumberBoxInput
                                    className='mx-4'
                                    initialValue={assessment.allocation.allocation_percent}
                                    errorMessage=
                                        {error? (error as any)[`assessments.${assessmentIndex}.allocation_percent`]: null}
                                    onValueChange={(value: any) => {
                                        setGradeSystemAssessments((prevAssessments) =>
                                            prevAssessments.map((prevAssessment, prevAssessmentIndex) =>
                                                prevAssessmentIndex === assessmentIndex
                                                    ? {
                                                        ...prevAssessment,
                                                        allocation: {
                                                            ...prevAssessment.allocation,
                                                            allocation_percent: value,
                                                        },
                                                    }
                                                    : prevAssessment
                                            )
                                        );
                                    }}
                                />
                                {
                                    (error && (error as any)[`assessments.${assessmentIndex}.name`]) &&

                                    <FormInputError>
                                        {(error as any)[`assessments.${assessmentIndex}.name`]}
                                    </FormInputError>
                                }
                            </div>
                        ))
                    }
                </>
                )}

                {/*Min passing score – only for rubric (last option)*/}
                {mode === 'rubric' && (
                    <div className='mt-4'>
                        <label className="d-flex align-items-center font-xs font-bold mb-1" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                className="me-2"
                                checked={enforceMinPassing}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setEnforceMinPassing(checked);
                                    if (checked && minPassing === null) setMinPassing(50);
                                }}
                            />
                            Set Min Passing Score
                        </label>
                        <div className='font-xs mb-2' style={{ color: '#6c757d' }}>
                            Minimum overall score (0-100) a student must reach to be considered passing.
                            When unchecked, no automatic pass/fail threshold is applied
                            (e.g. promotion will not retain students based on this).
                        </div>
                        {enforceMinPassing && (
                            <NumberBoxInput
                                initialValue={minPassing ?? 50}
                                onValueChange={(value: any) => setMinPassing(Number(value))}
                            />
                        )}
                    </div>
                )}
            </ModalWidget>

            <ModalWidget
                size={'sm'}
                title={'Grade Letters'}
                show={showSettings.gradeScore}
                onHide={()=>setShowSettingsType(GradingSystem.GRADE_SCORE, false)}
                onBack={()=>setShowSettingsType(GradingSystem.GRADE_SCORE, false)}
                onAction={handleSaveGradeSettings}
                description='Add, edit or remove grade letters'
                actionTitle={'Save Settings'}
                actionIcon={SaveSvg}
                headerIcon={GradingSystemSvg}
                navigateBack={false}
            >
                <div className='d-flex justify-content-end mb-2'>
                    <Button variant="outline-secondary" size="sm" className='me-2 font-bold'
                        onClick={() => setEditingAll(prev => ({...prev, gradeScore: !prev.gradeScore}))}
                    >
                        {editingAll.gradeScore ? 'Done Editing' : 'Edit All'}
                    </Button>
                    <Button variant="outline-danger" size="sm" className='font-bold'
                        onClick={() => handleDraftDeleteAll(GradingSystem.GRADE_SCORE)}
                    >
                        <TrashSvg className='me-1' style={{width: 14, height: 14}} />Delete All
                    </Button>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndGrades}>
                    <SortableContext items={draftGrades.map(g => g._id)} strategy={verticalListSortingStrategy}>
                        {draftGrades.map((grade, index) => (
                            <SortableFieldItem key={grade._id} id={grade._id}>
                                {({ listeners, attributes, setNodeRef, style }) => (
                                    <div ref={setNodeRef} style={style}>
                                        <FieldItem
                                            title={grade.name}
                                            isEditingItem={editingAll.gradeScore}
                                            dragListeners={listeners}
                                            dragAttributes={attributes}
                                            onValueChange={(newValue: string) => handleDraftFieldChange(GradingSystem.GRADE_SCORE, index, newValue)}
                                            onItemDeleted={() => handleDraftDelete(GradingSystem.GRADE_SCORE, index)}
                                        />
                                    </div>
                                )}
                            </SortableFieldItem>
                        ))}
                    </SortableContext>
                </DndContext>

                <div className='clickable mt-3 d-flex align-items-center'
                     onClick={() => handleDraftAdd(GradingSystem.GRADE_SCORE, {
                         name: 'C',
                         score: {
                             min_score: 0,
                             max_score: 0,
                         }
                     })}>
                    <PlusSvg/>
                    <div className='mx-2'>
                        Add Field
                    </div>
                </div>
            </ModalWidget>

            <ModalWidget
                size={'sm'}
                title={'Category Weights'}
                show={showSettings.scoreAllocation}
                onHide={()=>setShowSettingsType(GradingSystem.SCORE_ALLOCATION, false)}
                onBack={()=>setShowSettingsType(GradingSystem.SCORE_ALLOCATION, false)}
                onAction={handleSaveAssessmentSettings}
                description='Add, edit or remove categories'
                actionTitle={'Save Settings'}
                actionIcon={SaveSvg}
                headerIcon={GradingSystemSvg}
                navigateBack={false}
            >
                <div className='d-flex justify-content-end mb-2'>
                    <Button variant="outline-secondary" size="sm" className='me-2 font-bold'
                        onClick={() => setEditingAll(prev => ({...prev, scoreAllocation: !prev.scoreAllocation}))}
                    >
                        {editingAll.scoreAllocation ? 'Done Editing' : 'Edit All'}
                    </Button>
                    <Button variant="outline-danger" size="sm" className='font-bold'
                        onClick={() => handleDraftDeleteAll(GradingSystem.SCORE_ALLOCATION)}
                    >
                        <TrashSvg className='me-1' style={{width: 14, height: 14}} />Delete All
                    </Button>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndAssessments}>
                    <SortableContext items={draftAssessments.map(a => a._id)} strategy={verticalListSortingStrategy}>
                        {draftAssessments.map((assessment, index) => (
                            <SortableFieldItem key={assessment._id} id={assessment._id}>
                                {({ listeners, attributes, setNodeRef, style }) => (
                                    <div ref={setNodeRef} style={style}>
                                        <FieldItem
                                            title={assessment.name}
                                            isEditingItem={editingAll.scoreAllocation}
                                            dragListeners={listeners}
                                            dragAttributes={attributes}
                                            onValueChange={(newValue: string) =>
                                                handleDraftFieldChange(GradingSystem.SCORE_ALLOCATION, index, newValue)}
                                            onItemDeleted={() => handleDraftDelete(GradingSystem.SCORE_ALLOCATION, index)}
                                        />
                                    </div>
                                )}
                            </SortableFieldItem>
                        ))}
                    </SortableContext>
                </DndContext>

                <div className='clickable mt-3 d-flex align-items-center'
                    onClick={() => handleDraftAdd(GradingSystem.SCORE_ALLOCATION, {
                        name: mode === 'rubric' ? 'Category' : 'Assignment',
                        allocation: {
                            allocation_percent: 0
                        },
                    })}>
                    <PlusSvg/>
                    <div className='mx-2'>
                        Add Field
                    </div>
                </div>
            </ModalWidget>
        </>

    );
}

export default AddGradingSystem;
